import React, { useState, useEffect } from 'react';
import SeriesInfo from '../../components/series/SeriesInfo';
import { HeroCtas, ModelsGrid } from '../../components/series/SeriesModels';
import { bikeSpecs } from '../../data/bikeSpecs';
import { supabase } from '../../supabaseClient';
import { Link } from 'react-router-dom';
import { slugify, formatPrice, sortByPrice } from '../../utils';
import { ChevronLeft, ChevronRight, Activity, Cpu, Circle, Anchor } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';

const SeriesDetailArid = () => {
    const [bikes, setBikes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [swiperInstance, setSwiperInstance] = useState(null);

    useEffect(() => {
        const fetchBikes = async () => {
            try {
                const { data, error } = await supabase
                    .from('bicicletas')
                    .select('*')
                    .eq('serie_id', 19); // Arid ID

                if (error) throw error;
                setBikes(sortByPrice(data || []));

            } catch (error) {
                console.error('Error fetching Arid bikes:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBikes();
    }, []);

    if (loading) return <div className="h-screen flex items-center justify-center font-black uppercase tracking-tighter text-2xl">Cargando...</div>;

    const currentBike = bikes[activeIndex] || bikes[0];

    // Info for specs (using generic placeholders for now)
    const specs = currentBike ? [
        { label: 'Cuadro', value: bikeSpecs.cuadro(currentBike), icon: Circle },
        { label: 'Transmisión', value: bikeSpecs.transmision(currentBike), icon: Cpu },
        { label: 'Rueda', value: bikeSpecs.rueda(currentBike), icon: Activity },
        { label: 'Frenos', value: bikeSpecs.frenos(currentBike), icon: Anchor },
    ] : [];

    return (
        <div className="min-h-screen bg-white">

            {/* Top Section: Image, Carousel, Specs */}
            <div className="relative text-white pb-32 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img loading="lazy" decoding="async" src="https://wsrv.nl/?url=https%3A%2F%2Fb2b.raymon-bicycles.com%2Fmedia%2F39%2F4f%2F3f%2F1747034794%2FArid_hero.png&w=1400&output=webp&q=80"
                        alt="Background"
                        className="w-full h-full object-cover opacity-100"
                    />
                    <div className="absolute inset-0 bg-white/40" />
                </div>

                <div className="max-w-[1400px] mx-auto px-4 relative z-10 pt-32 text-center mb-8">
                    {/* Breadcrumbs */}
                    <div className="text-xs font-bold text-black uppercase tracking-widest mb-4">
                        Bicicletas / Series / Arid
                    </div>

                    {/* Visible Title */}
                    <h1 className="text-5xl md:text-7xl font-black italic text-center leading-none tracking-tight text-black drop-shadow-lg uppercase">
                        <span className="gradient-text-dark">ARID</span>
                    </h1>
                </div>

                {/* 3D Carousel Section - Full Width */}
                <div className="w-full relative z-10 mb-12">
                    <div className="relative h-[400px] md:h-[600px] flex items-center justify-center">
                        {bikes.length > 0 ? (
                            <Swiper
                                effect={'coverflow'}
                                grabCursor={true}
                                centeredSlides={true}
                                slidesPerView={'auto'}
                                loop={false}
                                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                                onSwiper={setSwiperInstance}
                                modules={[EffectCoverflow, Navigation]}
                                coverflowEffect={{
                                    rotate: 0,
                                    stretch: 0,
                                    depth: 100,
                                    modifier: 2.5,
                                    slideShadows: false,
                                }}
                                className="w-full h-full"
                            >
                                {bikes.map((bike, index) => (
                                    <SwiperSlide key={slugify(bike.modelo)} className={`!w-[85vw] md:!w-[800px] !h-full flex items-center justify-center transition-all duration-700 ${index === activeIndex ? 'scale-110 opacity-100 z-10' : 'scale-75 opacity-40 blur-[1px]'}`}>
                                        {({ isActive }) => (
                                            <div className="relative w-full h-full flex flex-col items-center justify-center">
                                                <Link to={`/product/${slugify(bike.modelo)}`} className="relative group block w-full">
                                                    <div className="relative aspect-[16/10] mb-8">
                                                        <img loading="lazy" decoding="async" src={bike.imagenes_urls?.[0]}
                                                            alt={bike.modelo}
                                                            className="w-full h-full object-contain drop-shadow-2xl"
                                                        />
                                                    </div>
                                                </Link>
                                            </div>
                                        )}
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        ) : (
                            <div className="text-center text-gray-500 font-bold uppercase tracking-widest">No models available for the Arid series yet.</div>
                        )}
                    </div>
                </div>

                {/* Centered Model Name & Price */}
                {currentBike && (
                    <div className="relative w-full text-center z-20 mb-12">
                        <h2 className="text-4xl font-black italic uppercase tracking-tight mb-2 text-black drop-shadow-lg">
                            {currentBike.modelo}
                        </h2>
                        <p className="text-lg text-gray-600 font-medium">
                            {formatPrice(currentBike.precio_eur, 'bikes')}
                        </p>
                        <HeroCtas bike={currentBike} />
                    </div>
                )}

                {/* Specs Bar with Navigation Buttons */}
                <div className="max-w-[1400px] mx-auto px-4 relative z-10 mb-20 hidden md:block">
                    <div className="flex items-center gap-4 md:gap-8 justify-center">
                        <button
                            onClick={() => swiperInstance?.slidePrev()}
                            className="p-3 rounded-full border border-black/20 hover:bg-black hover:text-white transition-all text-black disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!swiperInstance || swiperInstance.isBeginning}
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>

                        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 py-6 bg-white shadow-xl rounded-tl-[2rem] rounded-br-[2rem] px-4 md:px-8 text-black max-w-4xl mx-auto">
                            {specs.map((spec, idx) => (
                                <div key={idx} className="text-center">
                                    <spec.icon className="w-6 h-6 mx-auto mb-2 text-gray-800" />
                                    <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-widest mb-1">{spec.label}</div>
                                    <div className="text-base md:text-lg font-bold text-black max-w-full truncate px-1" title={spec.value}>{spec.value}</div>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => swiperInstance?.slideNext()}
                            className="p-3 rounded-full border border-black/20 hover:bg-black hover:text-white transition-all text-black disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!swiperInstance || swiperInstance.isEnd}
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>

            <ModelsGrid bikes={bikes} name="Arid" />

            <SeriesInfo slug="arid" images={["https://wsrv.nl/?url=https%3A%2F%2Fb2b.raymon-bicycles.com%2Fmedia%2F57%2F6b%2Ffa%2F1747138031%2FFrame&w=1400&output=webp&q=80 863.png", "https://wsrv.nl/?url=https%3A%2F%2Fb2b.raymon-bicycles.com%2Fmedia%2Fd9%2F80%2Fb3%2F1747138040%2FFrame&w=1400&output=webp&q=80 864.png"]} />

        </div>
    );
};

export default SeriesDetailArid;
